import { Cell, Row } from '@tanstack/react-table';
import classnames from 'classnames';
import * as React from 'react';
import { ReactNode, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { Checkbox } from '@components';

import styles from '../../Table.module.scss';
import { useTableContext } from '../../TableContext';
import { TableCol } from '../TableCol';

export type LastRowColumn = {
  cell: ReactNode | string;
  colSpan?: number;
};

type TableRowsProps<TData> = {
  onRowClick?: (
    event: React.MouseEvent<HTMLTableCellElement, MouseEvent>,
    cell: Cell<TData, unknown>,
    row: Row<TData>,
  ) => void;
  rowHrefFactory?: (row: Row<TData>) => string | null;
  lastRow?: LastRowColumn[];
};

const languageNamespace = 'table';

function TableRows<TData>({ onRowClick, rowHrefFactory, lastRow }: TableRowsProps<TData>) {
  const {
    getRowId,
    selected,
    isSelectable,
    select,
    isRowSelectable,
    getTable,
    columns,
    filteredData,
  } = useTableContext<TData>();

  const { t } = useTranslation(languageNamespace);

  const renderRow = (row: Row<TData>) => {
    const rowClickHandler = (
      event: React.MouseEvent<HTMLTableCellElement, MouseEvent>,
      cell: Cell<TData, unknown>,
    ) => {
      onRowClick?.(event, cell, row);
    };

    let href: string | null = null;
    if (rowHrefFactory) href = rowHrefFactory(row);

    const isSelected = selected.has(getRowId(row));
    const rowClassName = classnames(styles.row, {
      [styles.hasClickHandler]: !!onRowClick || href,
      selectedRow: isSelected,
    });

    const rowId = getRowId(row);

    return (
      <tr key={rowId} className={rowClassName}>
        {isSelectable && (
          <td className={classnames(styles.col, styles.selectCol)}>
            <Checkbox
              onChange={() => select(row)}
              checked={isSelected}
              name={`row-checkbox-${rowId}`}
              disabled={!isRowSelectable(rowId)}
            />
          </td>
        )}
        {row.getVisibleCells().map((cell) => (
          <TableCol<TData> cell={cell} key={cell.id} href={href} onClick={rowClickHandler} />
        ))}
      </tr>
    );
  };

  const renderRows = useCallback(() => {
    const rows: ReactNode[] = getTable()?.getRowModel()?.rows.map(renderRow) || [];

    if (rows.length === 0) {
      rows.push(
        <tr className={styles.row} key="no-data-row">
          <td className={classnames(styles.col, styles.center)} colSpan={columns.length + 1}>
            {t('noResultsFound')}
          </td>
        </tr>,
      );
    }

    if (lastRow) {
      rows.push(
        <tr key="last-row" className={styles.row}>
          {lastRow.map((column, index) => (
            <td className={styles.col} colSpan={column?.colSpan} key={`last-row-col-${index}`}>
              {column.cell}
            </td>
          ))}
        </tr>,
      );
    }
    return rows;
  }, [filteredData, selected, lastRow]);

  return renderRows();
}

export { TableRows };
